import { useEffect, useState } from "react";
import { Globe, User, Calendar, MapPin, Eye, X, Mountain, Lock, Download } from "lucide-react";
import * as pocketbaseService from "../utils/pocketbaseService";

// ✅ Cloud plan minimal : cloudId OBLIGATOIRE ici
type PublicCloudPlan = {
  cloudId: string;
  name?: string;
  location?: string;
  date?: string;
  raceType?: string;

  // aperçu
  thumbnail?: string | null;
  siteImage?: string | null;

  // stats UI
  elements?: any[];
  paths?: any[];

  // meta cloud
  userId: string;
  userName?: string;
  isPublic: boolean;
  isReadOnly?: boolean;
  updatedAt?: string;
  createdAt?: string;
};

interface SharedPlansLibraryProps {
  onClose: () => void;

  // ⚠️ onSelectPlan prend “plan complet” tel que renvoyé par getPlanWithImage(cloud)
  // On garde any pour ne pas recoller SkiPlan local ici (séparation claire).
  onSelectPlan: (plan: any) => void;

  currentUserId?: string;
  refreshKey: number;
}

export function SharedPlansLibrary({ onClose, onSelectPlan, currentUserId, refreshKey }: SharedPlansLibraryProps) {
  const [publicPlans, setPublicPlans] = useState<PublicCloudPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadPublicPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const loadPublicPlans = async () => {
    try {
      setError("");
      setIsLoading(true);

      const response = await pocketbaseService.getPublicPlans();
      if (response.error) throw new Error(response.error);

      const plans = (response.data?.plans || [])
        .filter((p: any) => !!p.cloudId) // sécurité
        .map((p: any) => ({
          cloudId: p.cloudId,
          name: p.name,
          location: p.location,
          date: p.date,
          raceType: p.raceType,
          thumbnail: p.thumbnail ?? null,
          siteImage: p.siteImage ?? null,
          elements: p.elements ?? [],
          paths: p.paths ?? [],
          userId: p.userId,
          userName: p.userName,
          isPublic: !!p.isPublic,
          isReadOnly: !!p.isReadOnly,
          updatedAt: p.updatedAt ?? p.updated,
          createdAt: p.createdAt ?? p.created,
        })) as PublicCloudPlan[];

      setPublicPlans(plans);
    } catch (err: any) {
      setError(err?.message || "Erreur chargement plans publics");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCloudPlan = async (cloudId: string) => {
    try {
        const response = await pocketbaseService.getPlanWithImage(cloudId);
      if (response.error) throw new Error(response.error);

      if (response.data?.plan) {
        // ✅ On ouvre le plan “cloud” directement dans l’éditeur
        // (l’éditeur gèrera canEdit/isReadOnly selon le record + user)
        onSelectPlan(response.data.plan);
        onClose();
      }
    } catch (err: any) {
      alert(`Erreur: ${err?.message || "Impossible d'ouvrir le plan"}`);
    }
  };

  const exportJson = async (cloudId: string) => {
    try {
        const res = await pocketbaseService.getPlanWithImage(cloudId);
      if (res.error) throw new Error(res.error);

      const plan = res.data?.plan;
      if (!plan) throw new Error("Plan introuvable");

      const json = JSON.stringify(plan, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `ski-plan-${cloudId}.json`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(`Erreur export: ${err?.message || "Impossible d'exporter"}`);
    }
  };

  const empty = !isLoading && !error && publicPlans.length === 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl">Plans Publics Partagés</h2>
              <p className="text-sm text-gray-600">Explorez les plans créés par la communauté</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des plans publics...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>
          ) : empty ? (
            <div className="text-center py-12">
              <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Aucun plan public disponible pour le moment</p>
              <p className="text-sm text-gray-500 mt-2">Soyez le premier à partager un plan avec la communauté !</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {publicPlans.map((plan) => (
                <div
                  key={plan.cloudId}
                  className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleOpenCloudPlan(plan.cloudId)}
                >
                  {/* Image d'aperçu */}
                  {plan.thumbnail || plan.siteImage ? (
                    <div className="h-40 bg-gray-100 relative overflow-hidden">
                      <img
                        src={(plan.thumbnail || plan.siteImage)!}
                        alt={plan.name || "Plan"}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs">
                        {(plan.elements?.length || 0).toString()} élément(s)
                      </div>

                      {plan.isReadOnly && (
                        <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Lecture seule
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-40 bg-gray-100 flex items-center justify-center">
                      <Mountain className="w-12 h-12 text-gray-300" />
                    </div>
                  )}

                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg line-clamp-1">{plan.name || "Plan sans nom"}</h3>
                      {plan.userId === currentUserId && (
                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded whitespace-nowrap ml-2">
                          Votre plan
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 mb-3">
                      {plan.userName && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 flex-shrink-0" />
                          <span className="line-clamp-1 text-gray-700 font-medium">{plan.userName}</span>
                        </div>
                      )}

                      {plan.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="line-clamp-1">{plan.location}</span>
                        </div>
                      )}

                      {plan.date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>{new Date(plan.date).toLocaleDateString("fr-FR")}</span>
                        </div>
                      )}

                      {plan.raceType && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">{plan.raceType}</span>
                        </div>
                      )}
                    </div>

                    <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>{plan.paths?.length || 0} tracés</span>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            exportJson(plan.cloudId);
                          }}
                          className="flex items-center gap-1 text-gray-700 hover:text-gray-900"
                          title="Exporter le plan en JSON"
                        >
                          <Download className="w-4 h-4" />
                          Export
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCloudPlan(plan.cloudId);
                          }}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                        >
                          <Eye className="w-4 h-4" />
                          Ouvrir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            💡 Astuce : Exportez un plan public en JSON si vous voulez en faire une copie locale.
          </p>
        </div>
      </div>
    </div>
  );
}